import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Home, 
  Home as HomeIcon,
  DollarSign, 
  Calculator, 
  FileText, 
  BarChart3,
  ArrowRight,
  TrendingUp,
  Target,
  Clock,
  AlertTriangle,
  Lock,
  ArrowUp,
  Calendar,
  HelpCircle,
  TrendingDown,
  Activity,
  Sparkles,
  Zap
} from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { useAuth } from '../../contexts/AuthContext';
import { mockDashboardAPI } from '../../services/mockDashboardAPI';
import AICoachBanner from './AICoachBanner';
import ActiveDealsCard from './ActiveDealsCard';
import ReflectionModal from './ReflectionModal';
import ActivityModal from './ActivityModal';
import FinancialOverviewModal from './FinancialOverviewModal';
import DashboardTourModal from './DashboardTourModal';
import axios from 'axios';
import Cookies from 'js-cookie';
import API_BASE_URL from '../../config/api';


const HomepagePanel = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [metrics, setMetrics] = useState({
    mortgage: { count: 0, loading: true },
    commission: { count: 0, loading: true },
    sellerNet: { count: 0, loading: true },
    investorPDFs: { count: 0, loading: true },
    pnlNet: { amount: 0, loading: true }
  });

  // Action Tracker data for Goals ↔ P&L Overview
  const [trackerData, setTrackerData] = useState({
    settings: null,
    dailyEntry: null,
    summary: null,
    pnlData: null,
    loading: true
  });

  // Commission Cap Progress data
  const [capProgress, setCapProgress] = useState({
    data: null,
    loading: true
  });

  // Modal states
  const [isReflectionModalOpen, setIsReflectionModalOpen] = useState(false);
  const [isActivityModalOpen, setIsActivityModalOpen] = useState(false);
  const [isFinancialOverviewModalOpen, setIsFinancialOverviewModalOpen] = useState(false);
  const [showFairyAICoach, setShowFairyAICoach] = useState(false);

  // Dashboard Tour state
  const [showTour, setShowTour] = useState(false);

  // Auto-launch tour for first-time users after onboarding
  useEffect(() => {
    if (!user?.id) return;
    const tourSeenKey = `inn_tour_seen_dashboard_${user.id}`;
    const hasSeenTour = localStorage.getItem(tourSeenKey);
    const justCompletedOnboarding = sessionStorage.getItem('onboarding_just_completed');
    
    if (!hasSeenTour && justCompletedOnboarding === 'true') {
      const timer = setTimeout(() => setShowTour(true), 800);
      return () => clearTimeout(timer);
    }
  }, [user?.id]);

  const handleTourClose = () => {
    setShowTour(false);
    if (user?.id) {
      localStorage.setItem(`inn_tour_seen_dashboard_${user.id}`, 'true');
    }
    sessionStorage.removeItem('onboarding_just_completed');
  };

  // Add Deal modal state (desktop only)
  const [showAddDeal, setShowAddDeal] = useState(false);
  const [leadSources, setLeadSources] = useState([]);
  const [newDeal, setNewDeal] = useState({
    house_address: '',
    amount_sold_for: '',
    commission_percent: '',
    split_percent: '',
    team_brokerage_split_percent: '',
    lead_source: '',
    contract_signed: '',
    due_diligence_start: '',
    due_diligence_over: '',
    closing_date: ''
  });

  // Get backend URL
  const backendUrl = API_BASE_URL;
  
  // Get current month in YYYY-MM format
  const getCurrentMonth = () => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  };

  // Get today's date in YYYY-MM-DD format
  const getTodayDate = () => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  };

  // Load metrics for all tools
  useEffect(() => {
    loadAllMetrics();
    loadTrackerData();
    loadCapProgress();
    loadLeadSources();
  }, [user]);

  // Load lead sources for Add Deal modal
  const loadLeadSources = async () => {
    try {
      const response = await axios.get(`${backendUrl}/api/pnl/lead-sources`, {
        withCredentials: true
      });
      setLeadSources(response.data || []);
    } catch (error) {
      console.error('Failed to load lead sources:', error);
      // Fallback lead sources
      setLeadSources(['Sphere of Influence', 'Referral', 'Open House', 'Online Lead', 'Sign Call', 'Past Client', 'Other']);
    }
  };

  // Handle add deal from homepage
  const handleAddDeal = async (e) => {
    e.preventDefault();
    try {
      const dealData = {
        ...newDeal,
        amount_sold_for: parseFloat(newDeal.amount_sold_for) || 0,
        commission_percent: parseFloat(newDeal.commission_percent) || 0,
        split_percent: parseFloat(newDeal.split_percent) || 100,
        team_brokerage_split_percent: parseFloat(newDeal.team_brokerage_split_percent) || 0
      };

      await axios.post(`${backendUrl}/api/pnl/deals`, dealData, {
        withCredentials: true
      });
      
      // Reset form
      setNewDeal({
        house_address: '',
        amount_sold_for: '',
        commission_percent: '',
        split_percent: '',
        team_brokerage_split_percent: '',
        lead_source: '',
        contract_signed: '',
        due_diligence_start: '',
        due_diligence_over: '',
        closing_date: ''
      });
      setShowAddDeal(false);
      
      // Reload metrics to reflect new deal
      loadAllMetrics();
    } catch (error) {
      console.error('Failed to add deal:', error);
      alert('Failed to add deal. Please try again.');
    }
  };

  const loadAllMetrics = async () => {
    try {
      // Load mortgage calculations count
      const mortgageData = await mockDashboardAPI.mortgage.history({ limit: 50 });
      setMetrics(prev => ({ 
        ...prev, 
        mortgage: { count: mortgageData.items.length, loading: false } 
      }));

      // Load commission splits count
      const commissionData = await mockDashboardAPI.commission.history({ limit: 50 });
      setMetrics(prev => ({ 
        ...prev, 
        commission: { count: commissionData.items.length, loading: false } 
      }));

      // Load seller net sheets count
      const netData = await mockDashboardAPI.net.history({ limit: 50 });
      setMetrics(prev => ({ 
        ...prev, 
        sellerNet: { count: netData.items.length, loading: false } 
      }));

      // Load investor PDFs count (Pro only)
      if (user?.plan === 'PRO') {
        const investorData = await mockDashboardAPI.investor.list({ limit: 50 });
        setMetrics(prev => ({ 
          ...prev, 
          investorPDFs: { count: investorData.items.length, loading: false } 
        }));

        // Load P&L net profit (Pro only)
        const pnlData = await mockDashboardAPI.pnl.summary();
        setMetrics(prev => ({ 
          ...prev, 
          pnlNet: { amount: pnlData.kpis.month.net, loading: false } 
        }));
      } else {
        setMetrics(prev => ({ 
          ...prev, 
          investorPDFs: { count: 0, loading: false },
          pnlNet: { amount: 0, loading: false }
        }));
      }

    } catch (error) {
      console.error('Failed to load dashboard metrics:', error);
      // Set all to not loading on error
      setMetrics(prev => ({
        mortgage: { count: 0, loading: false },
        commission: { count: 0, loading: false },
        sellerNet: { count: 0, loading: false },
        investorPDFs: { count: 0, loading: false },
        pnlNet: { amount: 0, loading: false }
      }));
    }
  };

  const loadTrackerData = async () => {
    try {
      const currentMonth = getCurrentMonth();
      const today = getTodayDate();

      // Load settings
      const settingsResponse = await axios.get(
        `${backendUrl}/api/tracker/settings?month=${currentMonth}`,
        { 
          withCredentials: true,
          headers: { 'Content-Type': 'application/json' }
        }
      );

      // Load daily entry
      const dailyResponse = await axios.get(
        `${backendUrl}/api/tracker/daily?date=${today}`,
        { 
          withCredentials: true,
          headers: { 'Content-Type': 'application/json' }
        }
      );

      // Load P&L data if Pro user
      let pnlData = null;
      if (user?.plan === 'PRO') {
        try {
          const pnlResponse = await axios.get(
            `${backendUrl}/api/pnl/summary?month=${currentMonth}`,
            { 
              withCredentials: true,
              headers: { 'Content-Type': 'application/json' }
            }
          );
          pnlData = {
            ...pnlResponse.data,
            deals_count: pnlResponse.data.deals?.length || 0
          };
        } catch (error) {
          console.error('P&L data not available:', error);
        }
      }

      setTrackerData({
        settings: settingsResponse.data,
        dailyEntry: dailyResponse.data.dailyEntry,
        summary: dailyResponse.data.summary,
        pnlData: pnlData,
        loading: false
      });

    } catch (error) {
      console.error('Error loading tracker data:', error);
      setTrackerData(prev => ({ ...prev, loading: false }));
    }
  };

  const loadCapProgress = async () => {
    try {
      const response = await axios.get(`${backendUrl}/api/cap-tracker/progress`, {
        withCredentials: true,
        headers: { 
          'Content-Type': 'application/json'
        }
      });
      
      setCapProgress({ data: response.data, loading: false });
    } catch (error) {
      // Cap progress is optional, don't show error if not configured
      console.log('Cap progress not available:', error);
      setCapProgress({ data: null, loading: false });
    }
  };

  // Get user's first name from full name or email
  const getFirstName = () => {
    // First try full_name
    if (user?.full_name && user.full_name.trim()) {
      return user.full_name.split(' ')[0];
    }
    // Then try name (for backward compatibility)
    if (user?.name && user.name.trim()) {
      return user.name.split(' ')[0];
    }
    // Extract from email as fallback
    if (user?.email) {
      const emailPrefix = user.email.split('@')[0];
      // Convert email prefix to a readable name
      const name = emailPrefix.charAt(0).toUpperCase() + emailPrefix.slice(1).replace(/[^a-zA-Z]/g, '');
      return name;
    }
    return 'Agent';
  };

  const formatCurrency = (cents) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(cents / 100);
  };

  // Format currency for Action Tracker
  const formatCurrencyAT = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  // GoalsMoneyOverview component was moved to FinancialOverviewModal

  const MetricCard = ({ icon: Icon, title, metric, subtext, loading, ctaText, onClick, isPro = false }) => (
    <Card className={`${isPro && user?.plan !== 'PRO' ? 'opacity-60' : ''} hover:shadow-md transition-shadow`}>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium text-gray-600">
          {title}
        </CardTitle>
        <Icon className="h-5 w-5 text-gray-400" />
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {loading ? (
            <div className="animate-pulse">
              <div className="h-8 bg-gray-200 rounded w-20"></div>
            </div>
          ) : (
            <div className="text-3xl font-bold text-gray-900">
              {metric}
            </div>
          )}
          <p className="text-sm text-gray-500">
            {subtext}
          </p>
          <Button 
            size="sm"
            onClick={onClick}
            disabled={isPro && user?.plan !== 'PRO'}
            className="w-full bg-gradient-to-r from-primary to-secondary hover:from-emerald-700 hover:to-emerald-800"
          >
            {ctaText}
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="h-full overflow-y-auto bg-gray-50">
      <div className="max-w-6xl mx-auto p-4 sm:p-6 lg:p-8 space-y-6">
        
        {/* Plan Indicator Badge */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Dashboard Overview</h1>
            <p className="text-gray-600 text-sm mt-1">Welcome back! Here's what's happening with your business.</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="dashboard-tour-replay-btn"
              onClick={() => setShowTour(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-emerald-200 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-xs font-medium transition-colors"
            >
              <Sparkles className="w-3.5 h-3.5" />
              Tour
            </button>
          </div>
        </div>
        
        {/* Header section removed - now handled in conditional content below */}

        {/* Conditional Content Based on User Plan */}
        {user?.plan === 'PRO' ? (
          <>
            {/* PRO Users: Action Buttons Row */}
            <div className="flex flex-wrap gap-4 justify-center mb-8">
              <Button
                onClick={async () => {
                  try {
                    const { fetchCoachJSON } = await import('../../lib/coach.js');
                    await fetchCoachJSON(true); // Force refresh
                    window.location.reload(); // Refresh to show updated data
                  } catch (error) {
                    console.error('Failed to refresh AI Coach:', error);
                    window.location.reload(); // Fallback to simple refresh
                  }
                }}
                className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white px-6 py-2 rounded-lg shadow-lg transition-all duration-300"
              >
                <div className="relative inline-block mr-2">
                  <Sparkles className="w-4 h-4 text-green-600" />
                  <Sparkles className="w-2 h-2 absolute -top-0.5 -right-0.5 animate-pulse" />
                </div>
                Refresh Fairy AI Coach
              </Button>
              
              <Button
                onClick={() => setShowAddDeal(true)}
                className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg shadow-lg transition-all duration-300"
                data-testid="homepage-add-deal-btn"
              >
                <HomeIcon className="w-4 h-4 mr-2" />
                Add Deal
              </Button>
              
              <Button
                onClick={() => setIsActivityModalOpen(true)}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg shadow-lg transition-all duration-300"
              >
                <Activity className="w-4 h-4 mr-2" />
                Log Activity
              </Button>
              
              <Button
                onClick={() => setIsReflectionModalOpen(true)}
                className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-2 rounded-lg shadow-lg transition-all duration-300"
              >
                <Calendar className="w-4 h-4 mr-2" />
                Log a Reflection
              </Button>
              
              {/* Financial & Activity Overview button - Hidden on desktop */}
              <Button
                onClick={() => setIsFinancialOverviewModalOpen(true)}
                className="lg:hidden bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-2 rounded-lg shadow-lg transition-all duration-300"
              >
                <BarChart3 className="w-4 h-4 mr-2" />
                Financial & Activity Overview
              </Button>
            </div>

            {/* AI Coach Banner for PRO users */}
            <AICoachBanner />

            {/* Active Deals Card - PRO users only */}
            {user?.plan === 'PRO' && (
              <ActiveDealsCard 
                onDealClick={(deal) => {
                  // Navigate to P&L panel
                  window.location.href = '/dashboard?panel=pnl';
                }}
              />
            )}

            {/* Financial Metrics Section - Desktop only, PRO users */}
            {user?.plan === 'PRO' && !trackerData.loading && (
              <div className="hidden lg:block mt-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Financial Progress</h3>
                <div className="grid grid-cols-4 gap-4">
                  {/* Profit This Month Card */}
                  <Card className="transition-all hover:shadow-md">
                    <CardContent className="p-4 text-center">
                      <div className="flex items-center justify-center mb-2">
                        <DollarSign className="w-5 h-5 text-emerald-600" />
                      </div>
                      <div className="text-2xl font-bold text-gray-900">
                        ${(trackerData.pnlData?.net_income || 0).toLocaleString()}
                      </div>
                      <div className="text-xs text-gray-600">Profit This Month</div>
                      <div className="text-xs text-gray-500">after all expenses</div>
                    </CardContent>
                  </Card>

                  {/* Activity Progress Card */}
                  <Card>
                    <CardContent className="p-4">
                      <div className="text-center mb-3">
                        <h4 className="font-medium text-gray-900 text-sm">Activity Progress</h4>
                        <p className="text-xs text-gray-500">Daily actions → closings</p>
                      </div>
                      <div className="relative">
                        {(() => {
                          // Calculate activity progress from P&L deals (actual closings) vs goal
                          const closingsThisMonth = trackerData.pnlData?.deals_count || 0;
                          const closingsGoal = trackerData.settings?.monthlyClosingsTarget || 
                            (trackerData.settings?.monthlyGciTarget && trackerData.settings?.avgGciPerClosing 
                              ? Math.ceil(trackerData.settings.monthlyGciTarget / trackerData.settings.avgGciPerClosing) 
                              : 4);
                          const activityProgress = closingsGoal > 0 ? Math.min(closingsThisMonth / closingsGoal, 1) : 0;
                          
                          return (
                            <>
                              <div className="w-full bg-gray-200 rounded-full h-4">
                                <div 
                                  className="bg-gradient-to-r from-blue-500 to-blue-600 h-4 rounded-full transition-all duration-500"
                                  style={{ width: `${activityProgress * 100}%` }}
                                />
                              </div>
                              <div className="text-center mt-2 text-xs font-medium">
                                {(activityProgress * 100).toFixed(0)}% to goal
                              </div>
                              <div className="text-center text-xs text-gray-500">
                                {closingsThisMonth} of {closingsGoal} closings
                              </div>
                            </>
                          );
                        })()}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Money Progress Card */}
                  <Card>
                    <CardContent className="p-4">
                      <div className="text-center mb-3">
                        <h4 className="font-medium text-gray-900 text-sm">Money Progress</h4>
                        <p className="text-xs text-gray-500">Monthly income goal</p>
                      </div>
                      <div className="relative">
                        {(() => {
                          // Calculate money progress from P&L income vs monthly goal
                          const totalIncome = trackerData.pnlData?.total_income || 0;
                          const monthlyGoal = trackerData.settings?.monthlyGciTarget || 10000;
                          const moneyProgress = monthlyGoal > 0 ? Math.min(totalIncome / monthlyGoal, 1) : 0;
                          
                          return (
                            <>
                              <div className="w-full bg-gray-200 rounded-full h-4">
                                <div 
                                  className="bg-gradient-to-r from-green-500 to-green-600 h-4 rounded-full transition-all duration-500"
                                  style={{ width: `${moneyProgress * 100}%` }}
                                />
                              </div>
                              <div className="text-center mt-2 text-xs font-medium">
                                {(moneyProgress * 100).toFixed(0)}% to goal
                              </div>
                              <div className="text-center text-xs text-gray-500">
                                ${totalIncome.toLocaleString()} of ${monthlyGoal.toLocaleString()}
                              </div>
                            </>
                          );
                        })()}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Commission Cap Progress Card */}
                  <Card>
                    <CardContent className="p-4">
                      <div className="text-center mb-3">
                        <h4 className="font-medium text-gray-900 text-sm">Commission Cap</h4>
                        <p className="text-xs text-gray-500">Annual cap progress</p>
                      </div>
                      <div className="relative">
                        {capProgress.loading ? (
                          <div className="w-full bg-gray-200 rounded-full h-4 animate-pulse"></div>
                        ) : capProgress.data ? (
                          <>
                            <div className="w-full bg-gray-200 rounded-full h-4">
                              <div 
                                className={`h-4 rounded-full transition-all duration-500 ${
                                  capProgress.data.is_complete 
                                    ? 'bg-gradient-to-r from-green-500 to-green-600' 
                                    : 'bg-gradient-to-r from-orange-500 to-orange-600'
                                }`}
                                style={{ width: `${Math.min(capProgress.data.percentage || 0, 100)}%` }}
                              />
                            </div>
                            <div className="text-center mt-2 text-xs font-medium">
                              {capProgress.data.is_complete ? (
                                <span className="text-green-600">Cap Complete!</span>
                              ) : (
                                `${(capProgress.data.percentage || 0).toFixed(0)}% to cap`
                              )}
                            </div>
                            <div className="text-center text-xs text-gray-500">
                              ${Math.round(capProgress.data.paid_so_far || 0).toLocaleString()} of ${Math.round(capProgress.data.total_cap || 0).toLocaleString()}
                            </div>
                          </>
                        ) : (
                          <div className="text-center text-xs text-gray-500">
                            <Target className="w-6 h-6 mx-auto mb-1 text-gray-300" />
                            <p>No cap configured</p>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            )}
          </>
        ) : (
          <>
            {/* STARTER/FREE Users: Simple Welcome Section */}
            <div className="mb-8">
              <div className="flex items-center mb-4">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-1">
                    Welcome {user?.full_name ? user.full_name.split(' ')[0] : user?.email?.split('@')[0] || 'User'}
                  </h2>
                </div>
              </div>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Get personalized insights that analyze your goals, activities, and performance 24/7. 
                Never miss opportunities or lose momentum again! Strategic advice to hit your targets faster.{' '}
                <Button
                  onClick={() => navigate('/pricing')}
                  className="inline-flex items-center bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white px-4 py-2 rounded-lg font-semibold shadow-sm transition-all duration-300 ml-2"
                >
                  <ArrowUp className="w-4 h-4 mr-1" />
                  Upgrade to Pro
                </Button>
              </p>
            </div>
          </>
        )}

        {/* Tool Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          
          {/* Mortgage Calculator Card - Hidden on desktop */}
          <div className="lg:hidden">
            <MetricCard
              icon={Home}
              title="Mortgage Calculator"
              metric={metrics.mortgage.count}
              subtext={metrics.mortgage.count === 0 ? "No calculations yet" : "Recent Calculations"}
              loading={metrics.mortgage.loading}
              ctaText="Go to Mortgage Tool"
              onClick={() => navigate('/dashboard?tab=mortgage')}
            />
          </div>

          {/* Commission Split Card - Hidden on desktop */}
          <div className="lg:hidden">
            <MetricCard
              icon={DollarSign}
              title="Commission Split"
              metric={metrics.commission.count}
              subtext={metrics.commission.count === 0 ? "No splits yet" : "Recent Splits"}
              loading={metrics.commission.loading}
              ctaText="Go to Commission Tool"
              onClick={() => navigate('/dashboard?tab=commission')}
            />
          </div>

          {/* Seller Net Sheet Card - Hidden on desktop */}
          <div className="lg:hidden">
            <MetricCard
              icon={Calculator}
              title="Seller Net Sheet"
              metric={metrics.sellerNet.count}
              subtext={metrics.sellerNet.count === 0 ? "No estimates yet" : "Recent Estimates"}
              loading={metrics.sellerNet.loading}
              ctaText="Go to Net Sheet Tool"
              onClick={() => navigate('/dashboard?tab=sellernet')}
            />
          </div>

          {/* Investor PDFs Card (Pro only) - Hidden on desktop */}
          <div className="lg:hidden">
            <MetricCard
              icon={FileText}
              title="Investor Deal PDFs"
              metric={metrics.investorPDFs.count}
              subtext={
                user?.plan !== 'PRO' 
                  ? "Upgrade to Pro" 
                  : metrics.investorPDFs.count === 0 
                    ? "No PDFs created yet" 
                    : "Investor Packets"
              }
              loading={metrics.investorPDFs.loading}
              ctaText={user?.plan !== 'PRO' ? "Upgrade to Pro" : "Go to Investor Tool"}
              onClick={() => {
                if (user?.plan !== 'PRO') {
                  navigate('/pricing');
                } else {
                  navigate('/dashboard?tab=investor');
                }
              }}
              isPro={true}
            />
          </div>

          {/* P&L Tracker Card (Pro only) - Hidden on desktop */}
          <div className="lg:hidden">
            <MetricCard
              icon={BarChart3}
              title="Agent P&L Tracker"
              metric={
                user?.plan !== 'PRO' 
                  ? "Pro Only" 
                  : metrics.pnlNet.amount === 0 
                    ? "$0" 
                    : formatCurrency(metrics.pnlNet.amount)
              }
              subtext={
                user?.plan !== 'PRO' 
                  ? "Upgrade to Pro" 
                  : metrics.pnlNet.amount === 0 
                    ? "No transactions yet" 
                    : "Monthly Net Profit"
              }
              loading={metrics.pnlNet.loading && user?.plan === 'PRO'}
              ctaText={user?.plan !== 'PRO' ? "Upgrade to Pro" : "Go to P&L Tool"}
              onClick={() => {
                if (user?.plan !== 'PRO') {
                  navigate('/pricing');
                } else {
                  navigate('/dashboard?tab=pnl');
                }
              }}
              isPro={true}
            />
          </div>

          {/* Quick Action Card - Hidden on desktop (lg screens) */}
          <Card className="lg:hidden bg-gradient-to-br from-primary/5 to-secondary/5 border-primary/20">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">
                Quick Actions
              </CardTitle>
              <TrendingUp className="h-5 w-5 text-primary" />
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="text-lg font-semibold text-gray-900">
                  Get Started
                </div>
                <p className="text-sm text-gray-600">
                  Run your first calculation or create an investor packet
                </p>
                <div className="space-y-2">
                  <Button 
                    size="sm"
                    variant="outline"
                    onClick={() => navigate('/calculator')}
                    className="w-full"
                  >
                    Free Calculator
                  </Button>
                  <Button 
                    size="sm"
                    onClick={() => navigate('/tools')}
                    className="w-full bg-gradient-to-r from-primary to-secondary hover:from-emerald-700 hover:to-emerald-800"
                  >
                    Browse All Tools
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Bottom CTA Section */}
        <div className="bg-white rounded-lg p-6 border border-gray-200">
          <div className="flex flex-col lg:flex-row items-center justify-between space-y-4 lg:space-y-0">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Need Help Getting Started?
              </h3>
              <p className="text-gray-600">
                Check out our support center or browse all available tools.
              </p>
            </div>
            <div className="flex space-x-3">
              <Button variant="outline" onClick={() => navigate('/support')}>
                Support Center
              </Button>
              {/* All Tools button - Hidden on desktop (lg screens) */}
              <Button 
                onClick={() => navigate('/tools')}
                className="lg:hidden bg-gradient-to-r from-primary to-secondary hover:from-emerald-700 hover:to-emerald-800"
              >
                All Tools
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Activity Modal */}
      <ActivityModal
        isOpen={isActivityModalOpen}
        onClose={() => setIsActivityModalOpen(false)}
        onActivitySaved={() => {
          // Optionally refresh any data that depends on activities
          console.log('Activity saved successfully');
        }}
      />

      {/* Reflection Modal */}
      <ReflectionModal
        isOpen={isReflectionModalOpen}
        onClose={() => setIsReflectionModalOpen(false)}
        onReflectionSaved={() => {
          // Optionally refresh any data that depends on reflections
          console.log('Reflection saved successfully');
        }}
      />

      {/* Financial Overview Modal */}
      <FinancialOverviewModal
        isOpen={isFinancialOverviewModalOpen}
        onClose={() => setIsFinancialOverviewModalOpen(false)}
        trackerData={trackerData}
        capProgress={capProgress}
        user={user}
      />

      {/* Add Deal Modal (Desktop Only) */}
      {showAddDeal && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-white shadow-2xl">
            <CardHeader>
              <CardTitle>Add New Deal</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleAddDeal} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="hp_house_address">Property Address *</Label>
                    <Input
                      id="hp_house_address"
                      value={newDeal.house_address}
                      onChange={(e) => setNewDeal(prev => ({ ...prev, house_address: e.target.value }))}
                      placeholder="123 Main St, City, State"
                      className="mt-1"
                      required
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="hp_amount_sold_for">Amount Sold For *</Label>
                    <Input
                      id="hp_amount_sold_for"
                      type="number"
                      value={newDeal.amount_sold_for}
                      onChange={(e) => setNewDeal(prev => ({ ...prev, amount_sold_for: e.target.value }))}
                      placeholder="500000"
                      className="mt-1"
                      required
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="hp_commission_percent">Commission % *</Label>
                    <Input
                      id="hp_commission_percent"
                      type="number"
                      step="0.1"
                      value={newDeal.commission_percent}
                      onChange={(e) => setNewDeal(prev => ({ ...prev, commission_percent: e.target.value }))}
                      placeholder="6.0"
                      className="mt-1"
                      required
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="hp_split_percent">Agent's Share % (Default: 100%)</Label>
                    <Input
                      id="hp_split_percent"
                      type="number"
                      step="0.1"
                      value={newDeal.split_percent}
                      onChange={(e) => setNewDeal(prev => ({ ...prev, split_percent: e.target.value }))}
                      placeholder="100.0"
                      className="mt-1"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      What % of total commission you receive (leave blank for 100%)
                    </p>
                  </div>
                  
                  <div>
                    <Label htmlFor="hp_team_brokerage_split_percent">Team/Brokerage Split % *</Label>
                    <Input
                      id="hp_team_brokerage_split_percent"
                      type="number"
                      step="0.1"
                      value={newDeal.team_brokerage_split_percent}
                      onChange={(e) => setNewDeal(prev => ({ ...prev, team_brokerage_split_percent: e.target.value }))}
                      placeholder="20.0"
                      className="mt-1"
                      required
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="hp_lead_source">Lead Source *</Label>
                    <select
                      id="hp_lead_source"
                      value={newDeal.lead_source}
                      onChange={(e) => setNewDeal(prev => ({ ...prev, lead_source: e.target.value }))}
                      className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                      required
                    >
                      <option value="">Select lead source</option>
                      {leadSources.map(source => (
                        <option key={source} value={source}>{source}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <Label htmlFor="hp_contract_signed">Contract Signed *</Label>
                    <Input
                      id="hp_contract_signed"
                      type="date"
                      value={newDeal.contract_signed}
                      onChange={(e) => setNewDeal(prev => ({ ...prev, contract_signed: e.target.value }))}
                      className="mt-1"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="hp_due_diligence_start">Due Diligence Start *</Label>
                    <Input
                      id="hp_due_diligence_start"
                      type="date"
                      value={newDeal.due_diligence_start}
                      onChange={(e) => setNewDeal(prev => ({ ...prev, due_diligence_start: e.target.value }))}
                      className="mt-1"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="hp_due_diligence_over">Due Diligence Over *</Label>
                    <Input
                      id="hp_due_diligence_over"
                      type="date"
                      value={newDeal.due_diligence_over}
                      onChange={(e) => setNewDeal(prev => ({ ...prev, due_diligence_over: e.target.value }))}
                      className="mt-1"
                      required
                    />
                  </div>
                  
                  <div className="md:col-span-2">
                    <Label htmlFor="hp_closing_date">Closing Date *</Label>
                    <Input
                      id="hp_closing_date"
                      type="date"
                      value={newDeal.closing_date}
                      onChange={(e) => setNewDeal(prev => ({ ...prev, closing_date: e.target.value }))}
                      className="mt-1"
                      required
                    />
                  </div>
                </div>
                
                <div className="flex space-x-2 pt-4">
                  <Button
                    type="submit"
                    className="flex-1 bg-green-600 hover:bg-green-700"
                    data-testid="homepage-add-deal-submit"
                  >
                    Add Deal
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowAddDeal(false)}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Dashboard Tour Modal */}
      <DashboardTourModal isOpen={showTour} onClose={handleTourClose} />
    </div>
  );
};

export default HomepagePanel;